import { planBayWidthOf } from "@CommonUtils/cdtStudio/domain/aestheticsScore";
import type { StudioRunGeometry } from "@CommonUtils/cdtStudio/domain/studioTypes";

/* A fixed length is a different distance in each raw unit, so every slack the Studio compares
   against is a share of the wall's own bay, calibrated on a 48 in bay of 4800 raw units: an inch
   plan reads exactly as before, and the same plan in hundredths of a centimetre reads alike. */
const CALIBRATION_BAY = 4800;

/** `units` of a 4800-wide bay, as the same share of `bayWidth`. */
export const bayShare = (units: number, bayWidth: number): number =>
  (units / CALIBRATION_BAY) * bayWidth;

/** A run's own bay: its stated bay, else the plan's (`planBayWidthOf`). */
export const runBayWidthOf = (
  run: StudioRunGeometry | undefined,
  runs: readonly StudioRunGeometry[] = run ? [run] : [],
): number => (run && run.bayWidthCm > 0 ? run.bayWidthCm : planBayWidthOf(runs));

/** The Studio's slacks on a bay `bayWidth` wide (none stated: `planBayWidthOf` a plan with no
 *  bays). */
export const slacksOf = (bayWidth: number | undefined) => {
  const bay = bayWidth && bayWidth > 0 ? bayWidth : planBayWidthOf([]);
  return {
    /** Float-level fit: 0.4 of a 4800 bay. */
    fit: bayShare(0.4, bay),
    /** A height or a shift too small to count: 2. */
    nudge: bayShare(2, bay),
    /** Fill, opening and edge slack: 20. */
    fill: bayShare(20, bay),
    /** A bay line grazed, and the floor under a pack with no width: 39. */
    edge: bayShare(39, bay),
    /** A row this far short of its run, or a block's spill past its window, is air: 472. */
    air: bayShare(472, bay),
  };
};

export type Slacks = ReturnType<typeof slacksOf>;

/** `slacksOf` the plan's own bay. */
export const planSlacksOf = (runs: readonly StudioRunGeometry[]): Slacks =>
  slacksOf(planBayWidthOf(runs));

/* Whether two edges line up (two shelves' runs, a brand's line, one stretch or two) is measured
   in the widest pack of what is compared, about a facing, not the bay: the same packs line up
   alike on a 48 in and a 96 in bay. Each k gives the old fixed value on a 944-wide pack, a
   typical laundry or facial tissue facing on a 48 in bay. */
export const PACK_SLACK = {
  /** A brand line still straight: 79 on a 944 pack. */
  line: 1 / 12,
  /** A gap under it is the same stretch: 472 on a 944 pack. */
  stretch: 1 / 2,
} as const;

/** `k` of the widest pack of what is compared. */
export const packSlackOf = (widestPack: number, k: number): number => k * widestPack;
